// Pure helpers for the reload→restore sequence. No React, no sessionStorage side
// effects except readRestoreSignals (which is injectable for tests).

import { ACTIVE_FORECAST_KEY, PENDING_FORECAST_KEY, TOUR_STATE_KEY } from "./storageKeys.js";

const _store = () => (typeof sessionStorage !== "undefined" ? sessionStorage : null);

/** Read the three restore signals synchronously (safe in SSR/tests). */
export function readRestoreSignals(storage = _store()) {
  if (!storage) return { activeId: null, pendingId: null, tour: null };
  let pendingId = null;
  try {
    const raw = storage.getItem(PENDING_FORECAST_KEY);
    pendingId = raw ? (JSON.parse(raw)?.clientId ?? null) : null;
  } catch {
    pendingId = null;
  }
  let activeId = null;
  try {
    activeId = storage.getItem(ACTIVE_FORECAST_KEY) || null;
  } catch {
    activeId = null;
  }
  let tour = null;
  try {
    const raw = storage.getItem(TOUR_STATE_KEY);
    const p = raw ? JSON.parse(raw) : null;
    tour = p && typeof p === "object" ? p : null;
  } catch {
    tour = null;
  }
  return { activeId, pendingId, tour };
}

export function hasPendingRestore(signals) {
  return !!(
    signals &&
    (signals.activeId || signals.pendingId || (signals.tour && signals.tour.active))
  );
}

/**
 * Pure priority decision for what to restore after hydration.
 * Priority: active tour > in-flight self-submitted > last-viewed > none.
 */
export function decideRestore(signals, runsById = {}) {
  // Restore an active tour when EITHER it is still pre-forecast (no example
  // forecast row exists yet — exampleForecastId is null; resumeTour restarts it)
  // OR its example forecast hydrated into the store (resume at the saved step).
  // A tour whose exampleForecastId is set but is NOT in runsById is stale (the
  // forecast was deleted) — fall through and let a later signal decide.
  const t = signals?.tour;
  if (t && t.active && (t.exampleForecastId == null || runsById[t.exampleForecastId])) {
    return { kind: "tour", tour: t };
  }
  if (signals?.pendingId) {
    // A pre-trace in-flight submit (no traceId yet) can only be recovered here —
    // the resume sweep needs a traceId — so it still reattaches (and focuses).
    return { kind: "reattach", pendingId: signals.pendingId };
  }
  // Restore the LAST-VIEWED forecast so a refresh stays on the forecast the user
  // had open (rather than booting to the blank home). Only when it hydrated into
  // the store — a stale handle whose forecast was deleted/isn't owned falls
  // through to blank. A still-running root reattaches (keep streaming + refocus);
  // a settled one is selected (thread rehydrate + timeline reframe). Status
  // strings mirror model.js STATUS (kept as literals to keep this module
  // dependency-light, matching forecastBadge.js).
  const activeId = signals?.activeId;
  const run = activeId ? runsById[activeId] : null;
  if (run) {
    const running = run.status === "running" || run.status === "preprocessing";
    return running && run.traceId
      ? { kind: "reattach", runId: activeId }
      : { kind: "select", runId: activeId };
  }
  return { kind: "none" };
}

/** The render gate: show the introduction at `ready`, OR immediately when there is
 *  nothing to restore (`!pendingRestore`); never while a restore is pending. */
export function shouldShowIntroduction({ bootPhase, hasActive, pendingRestore }) {
  if (hasActive) return false;
  if (!pendingRestore) return true; // genuine new user / no last forecast → immediate, no flash
  return bootPhase === "ready"; // restore pending → wait until done
}
